import CustomField from "components/common/CustomField";
import CustomLabel from "components/common/CustomLabel";
import DetailsPageTitle from "components/common/DetailsPageTitle";
import FileUpload from "components/common/FileUpload";
import SingleSelect from "components/common/SingleSelect";
import { createWorkspaceValues } from "components/constants/InitialFormValues";
import { ErrorMessage, FieldArray, Form, Formik } from "formik";
import { CircleX } from "lucide-react";
import React, { useEffect, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import LineIcon from "assets/images/icons/2Line.svg";
import MultipleSelect from "components/common/MultipleSelect";
import { addWorkspaceSchema } from "components/common/Validation";
import FormButtons from "components/common/FormButtons";
import { useDispatch, useSelector } from "react-redux";
import { getCityList } from "store/actions/citySlice";
import { getBranchListByCity } from "store/actions/branchSlice";
import { getAmenityList } from "store/actions/amenitySlice";
import { showAlert } from "store/reducers/alertReducer";
import {
  addWorkspace,
  getWorkspaceDetails,
  updateWorkspace,
} from "store/actions/workspaceSlice";
import { useNavigate, useSearchParams } from "react-router-dom";
import Spinners from "components/common/Spinners";
import { parse, differenceInMinutes, isBefore, isEqual } from "date-fns";
import CustomTimeRangePicker from "components/common/CustomTimeRangePicker";

const AddWorkspace = ({ mode }) => {
  const { t } = useTranslation();
  document.title = t("Add Workspace | Work Space");

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");

  const [formValues, setFormValues] = useState(createWorkspaceValues);

  const { isLoading, loading, workspaceDetails } = useSelector(
    (state) => state.Workspace
  );

  const { citiesList } = useSelector((state) => state.Cities);
  const { branchesByCity } = useSelector((state) => state.Branches);
  const { amenitiesList } = useSelector((state) => state.Amenities);

  useEffect(() => {
    // GET CITY LIST
    const fetchCityList = () => {
      dispatch(getCityList());
    };
    // GET AMENITIES LIST
    const fetchAmenitiesList = () => {
      dispatch(getAmenityList());
    };

    const getDetails = () => {
      const payload = {
        id,
      };
      dispatch(getWorkspaceDetails(payload));
    };

    fetchCityList();
    fetchAmenitiesList();
    mode === "edit" && getDetails();
  }, [mode]);

  // UPDATE FORM FIELD IN EDIT MODE
  useEffect(() => {
    if (workspaceDetails && !loading && mode === "edit") {
      const {
        amenities,
        branchId,
        cityId,
        description,
        descriptionAr,
        imageUrls,
        name,
        nameAr,
        spaceTypeInt,
        status,
        timePeriods,
        totalSeats,
      } = workspaceDetails || {};
      // get branches by city
      cityId?._id && fetchBranchesByCity(cityId?._id);

      setFormValues({
        ...formValues,
        spaceTypeInt: 1,
        spaceType: "workSpace",
        name,
        nameAr,
        description,
        descriptionAr,
        imageUrls,
        cityId: cityId?._id,
        branchId: branchId?.id,
        amenities: [],
        status, // 0: Deactive, 1: Active, 2: Delete, 3: Draft
        timePeriods,
      });
    }
  }, [workspaceDetails]);

  const fetchBranchesByCity = (cityId) => {
    const payload = {
      cityId,
    };
    dispatch(getBranchListByCity(payload));
  };

  useEffect(() => {
    if (mode === "edit" && workspaceDetails) {
      // Destructure keys from meetingDetails
      const {
        spaceTypeInt,
        spaceType,
        name,
        nameAr,
        description,
        descriptionAr,
        imageUrls,
        cityId,
        branchId,
        amenities,
        status,
        timePeriods,
      } = workspaceDetails || {};

      const updatedAmenities = amenities?.map((amenity) => amenity?._id) || [];

      // Update formValues using destructured values
      setFormValues((prevValues) => ({
        ...prevValues,
        spaceTypeInt,
        spaceType,
        name,
        nameAr,
        description,
        descriptionAr,
        imageUrls,
        cityId: cityId?._id,
        branchId: branchId?._id || "",
        amenities: updatedAmenities,
        status,
        timePeriods,
      }));
    }
  }, [mode, workspaceDetails]);

  const handleOnSubmit = (values, { setSubmitting }) => {
    const buttonId = document.activeElement.id;
    // UPDATE STATUS ACCORDING TO ACTION TYPE
    const updatedStatus =
      buttonId === "draftBtn" ? 3 : buttonId === "submitBtn" ? 1 : 1;
    const {
      name,
      nameAr,
      description,
      descriptionAr,
      imageUrls,
      cityId,
      branchId,
      amenities,
      status,
      timePeriods,
    } = values;

    const errors = validateTimePeriods(values.timePeriods);

    if (errors.length > 0) {
      dispatch(showAlert({ text: errors.join("\n"), type: "error" }));
      return;
    }

    let data = new FormData();
    data.append("spaceTypeInt", "1");
    data.append("spaceType", "workSpace");
    data.append("name", name);
    data.append("nameAr", nameAr);
    data.append("description", description);
    data.append("descriptionAr", descriptionAr);
    const filesToUpload = imageUrls.map((fileObj) => fileObj);
    filesToUpload.forEach((file) => {
      data.append("imageUrls", file);
    });
    data.append("cityId", cityId);
    data.append("branchId", branchId);

    amenities.forEach((amenity, index) => {
      data.append(`amenities[${index}]`, amenity);
    });

    timePeriods.forEach((period, index) => {
      data.append(`timePeriods[${index}][name]`, period.name);
      data.append(`timePeriods[${index}][nameAr]`, period.nameAr);
      data.append(`timePeriods[${index}][numberOfSeats]`, period.numberOfSeats);
      data.append(`timePeriods[${index}][price]`, period.price);
      data.append(`timePeriods[${index}][discount]`, period.discount);
      data.append(`timePeriods[${index}][time][start]`, period.time.start);
      data.append(`timePeriods[${index}][time][end]`, period.time.end);
    });

    data.append("status", updatedStatus);

    if (mode === "add") {
      dispatch(addWorkspace(data))
        .then((action) => {
          const { statusCode, success, message } = action?.payload;
          if (statusCode === 200 && success) {
            dispatch(showAlert({ text: message, type: "success" }));
            navigate(-1);
          } else {
            dispatch(showAlert({ text: message, type: "error" }));
          }
        })
        .catch((err) => {});
    } else if (mode === "edit") {
      data.append("id", id);
      dispatch(updateWorkspace(data))
        .then((action) => {
          const { statusCode, success, message } = action?.payload;
          if (statusCode === 200 && success) {
            dispatch(showAlert({ text: message, type: "success" }));
            navigate(-1);
          } else {
            dispatch(showAlert({ text: message, type: "error" }));
          }
        })
        .catch((err) => {});
    }
  };

  const validateTimePeriods = (timePeriods) => {
    const errors = [];

    timePeriods.forEach((period, index) => {
      const { start, end } = period.time;

      // Use today's date to parse time (same for both)
      const today = new Date();
      const startDate = parse(start, "hh:mm a", today);
      const endDate = parse(end, "hh:mm a", today);

      // 1. Start time must be before end time
      if (!isBefore(startDate, endDate)) {
        errors.push(
          `Slot ${index + 1}: Start time must be earlier than end time.`
        );
        return;
      }

      // 2. Start time and End time must not be equal
      if (isEqual(startDate, endDate)) {
        errors.push(
          `Slot ${index + 1}: Start time and end time cannot be the same.`
        );
        return;
      }

      // 3. Difference must be full hours only (e.g., 60, 120, 180, etc.)
      const diff = differenceInMinutes(endDate, startDate);
      if (diff % 60 !== 0) {
        errors.push(
          `Slot ${
            index + 1
          }: Time slot must be in full hours (e.g., 1hr, 2hr, 3hr).`
        );
        return;
      }

      // 4. Should not end at 12:00 AM (crossing midnight not allowed)
      if (end === "12:00 AM") {
        errors.push(
          `Slot ${
            index + 1
          }: Time slot cannot end at 12:00 AM (crossing midnight not allowed).`
        );
        return;
      }
    });

    return errors;
  };

  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  const handleDragStart = (index) => setDraggedIndex(index);
  const handleDragOver = (e, index) => {
    e.preventDefault();
    setDragOverIndex(index);
  };
  const handleDrop = (e, setFieldValue, values) => {
    e.preventDefault();

    const newItems = [...values.timePeriods];
    const draggedItem = newItems[draggedIndex];

    newItems.splice(draggedIndex, 1);
    newItems.splice(dragOverIndex, 0, draggedItem);

    setFieldValue("timePeriods", newItems);
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  return (
    <React.Fragment>
      <div className="page-content common_page add_workspace_page">
        {loading && mode === "edit" ? (
          <Spinners />
        ) : (
          <Container fluid>
            <Row>
              <DetailsPageTitle
                title={
                  mode === "add" ? t("Add New Workspace") : t("Edit Workspace")
                }
              />
            </Row>
            <Formik
              initialValues={formValues}
              validationSchema={addWorkspaceSchema}
              enableReinitialize={true}
              onSubmit={handleOnSubmit}
            >
              {({ values, setFieldValue, errors, touched }) => (
                <Form>
                  <Row className="common_wrapper">
                    <Col lg={12}>
                      <FileUpload
                        uploadedFiles={values.imageUrls || []}
                        setUploadedFiles={(files) =>
                          setFieldValue("imageUrls", files)
                        }
                        maxFiles={5}
                        maxFileSize={0.6 * 1024 * 1024}
                        acceptedFileTypes={[
                          "image/png",
                          "image/jpeg",
                          "image/svg+xml",
                        ]}
                        requireSquare={false}
                        requireRectangle={false}
                        // onChange={handleFilesChange}
                        className={
                          touched?.imageUrls && errors.imageUrls
                            ? "invalid"
                            : ""
                        }
                      />
                    </Col>
                    <Col lg={6}>
                      <div className="form_field">
                        <CustomLabel
                          htmlFor="name"
                          label={t("English Title")}
                        />
                        <CustomField
                          name="name"
                          type="text"
                          placeholder={t("Enter English Title")}
                          touched={touched}
                          errors={errors}
                        />
                      </div>
                    </Col>
                    <Col lg={6}>
                      <div className="form_field">
                        <CustomLabel
                          htmlFor="nameAr"
                          label={t("Arabic Title")}
                        />
                        <CustomField
                          name="nameAr"
                          type="text"
                          placeholder={t("Enter Arabic Title")}
                          touched={touched}
                          errors={errors}
                          isArabic={true}
                        />
                      </div>
                    </Col>
                    <Col lg={6}>
                      <div className="form_field">
                        <CustomLabel
                          htmlFor="description"
                          label={t("English Description")}
                        />
                        <CustomField
                          name="description"
                          placeholder="Enter English Description"
                          touched={touched}
                          errors={errors}
                          as="textarea"
                        />
                      </div>
                    </Col>
                    <Col lg={6}>
                      <div className="form_field">
                        <CustomLabel
                          htmlFor="descriptionAr"
                          label={t("Arabic Description")}
                        />
                        <CustomField
                          name="descriptionAr"
                          placeholder="Enter Arabic Description"
                          touched={touched}
                          errors={errors}
                          as="textarea"
                          isArabic={true}
                        />
                      </div>
                    </Col>
                    <Col lg={12}>
                      <div className="bg_white br_14 p_23_30">
                        <h2 className="fs_20 mb-4">{t("Location")}</h2>
                        <Row>
                          <Col lg={6}>
                            <div className="form_field">
                              <CustomLabel htmlFor="cityId" label={t("City")} />
                              <SingleSelect
                                options={citiesList?.map((item) => ({
                                  name: item?.name,
                                  value: item?._id,
                                }))}
                                value={values.cityId}
                                placeholder={t("Select City")}
                                handleSingleSelectChange={(e) => {
                                  fetchBranchesByCity(e.value);
                                  setFieldValue("cityId", e.value);
                                  setFieldValue("branchId", null);
                                }}
                                className={`${
                                  errors.cityId && touched.cityId
                                    ? "invalid"
                                    : ""
                                }`}
                                error={errors?.cityId}
                              />
                            </div>
                          </Col>
                          <Col lg={6}>
                            <div className="form_field">
                              <CustomLabel
                                htmlFor="branchId"
                                label={t("Branch")}
                              />
                              <SingleSelect
                                options={branchesByCity?.map((item) => ({
                                  name: item?.name,
                                  value: item?._id,
                                }))}
                                value={values.branchId}
                                placeholder={t("Select Branch")}
                                handleSingleSelectChange={(e) => {
                                  setFieldValue("branchId", e.value);
                                }}
                                className={`${
                                  errors.branchId && touched.branchId
                                    ? "invalid"
                                    : ""
                                }`}
                                error={errors?.branchId}
                              />
                            </div>
                          </Col>
                        </Row>
                      </div>
                    </Col>
                    <Col lg={12} style={{ marginTop: 21 }}>
                      <div className="bg_white br_14 p_23_30">
                        <h2 className="fs_20">{t("Time Periods")}</h2>
                        <Row className="m-0">
                          <FieldArray
                            name="timePeriods"
                            render={(arrayHelpers) => (
                              <>
                                {values?.timePeriods?.map(
                                  (timePeriod, index) => (
                                    <div
                                      className="d-flex mt-4"
                                      key={index}
                                      draggable
                                      onDragStart={() => handleDragStart(index)}
                                      onDragOver={(e) =>
                                        handleDragOver(e, index)
                                      }
                                      onDrop={(e) =>
                                        handleDrop(e, setFieldValue, values)
                                      }
                                      style={{
                                        backgroundColor:
                                          dragOverIndex === index
                                            ? "#f1f3f5"
                                            : "transparent",
                                        borderRadius: 6,
                                        padding: 8,
                                      }}
                                    >
                                      <img
                                        src={LineIcon}
                                        alt="icon"
                                        width={18}
                                        height={10}
                                        style={{
                                          marginRight: 20,
                                          cursor: "grab",
                                        }}
                                      />
                                      <div
                                        className="time_period_inner"
                                        style={{ flexGrow: 1 }}
                                      >
                                        <Col
                                          lg={12}
                                          className="align-items-center mb-3"
                                        >
                                          <Row>
                                            <Col>
                                              <div className="form_field">
                                                <CustomLabel
                                                  htmlFor={`timePeriods[${index}].name`}
                                                  label={t("English Title")}
                                                />
                                                <CustomField
                                                  name={`timePeriods[${index}].name`}
                                                  type="text"
                                                  placeholder={t(
                                                    "Enter English Title"
                                                  )}
                                                  touched={touched}
                                                  errors={errors}
                                                />
                                              </div>
                                            </Col>
                                            <Col>
                                              <div className="form_field">
                                                <CustomLabel
                                                  htmlFor={`timePeriods[${index}].nameAr`}
                                                  label={t("Arabic Title")}
                                                />
                                                <CustomField
                                                  name={`timePeriods[${index}].nameAr`}
                                                  type="text"
                                                  placeholder={t(
                                                    "Enter Arabic Title"
                                                  )}
                                                  touched={touched}
                                                  errors={errors}
                                                />
                                              </div>
                                            </Col>
                                            <Col>
                                              <div className="form_field">
                                                <CustomLabel
                                                  htmlFor={`timePeriods[${index}].numberOfSeats`}
                                                  label={t("Number of Seat")}
                                                />
                                                <CustomField
                                                  name={`timePeriods[${index}].numberOfSeats`}
                                                  type="number"
                                                  placeholder={t(
                                                    "Enter Number of Seat"
                                                  )}
                                                  touched={touched}
                                                  errors={errors}
                                                />
                                              </div>
                                            </Col>
                                            <Col>
                                              <div className="form_field">
                                                <CustomLabel
                                                  htmlFor={`timePeriods[${index}].price`}
                                                  label={t("Price")}
                                                />
                                                <CustomField
                                                  name={`timePeriods[${index}].price`}
                                                  type="number"
                                                  placeholder={t("Price")}
                                                  touched={touched}
                                                  errors={errors}
                                                />
                                              </div>
                                            </Col>
                                            <Col>
                                              <div className="form_field">
                                                <CustomLabel
                                                  htmlFor={`timePeriods[${index}].discount`}
                                                  label={t("Discount Price")}
                                                />
                                                <CustomField
                                                  name={`timePeriods[${index}].discount`}
                                                  type="number"
                                                  placeholder={t(
                                                    "Discount Price"
                                                  )}
                                                  touched={touched}
                                                  errors={errors}
                                                />
                                              </div>
                                            </Col>
                                          </Row>
                                          <Row>
                                            <Col lg={10}>
                                              <div className="position-relative">
                                                <p className="fs_16 text_dark_gray mb-2">
                                                  {t("Time")}
                                                </p>
                                                <div className="time_inner_section d-flex gap-5 align-items-center">
                                                  <CustomTimeRangePicker
                                                    startTime={
                                                      timePeriod?.time?.start ||
                                                      "12:00 AM"
                                                    }
                                                    endTime={
                                                      timePeriod?.time?.end ||
                                                      "12:00 PM"
                                                    }
                                                    onStartChange={(val) =>
                                                      setFieldValue(
                                                        `timePeriods[${index}].time.start`,
                                                        val
                                                      )
                                                    }
                                                    onEndChange={(val) =>
                                                      setFieldValue(
                                                        `timePeriods[${index}].time.end`,
                                                        val
                                                      )
                                                    }
                                                    className="big_time_range_picker"
                                                  />
                                                </div>
                                                <div
                                                  className="position-absolute"
                                                  style={{ top: 40, right: 10 }}
                                                >
                                                  {values?.timePeriods?.length >
                                                    1 && (
                                                    <button
                                                      type="button"
                                                      className="transparent_btn text_danger p-0"
                                                      onClick={() =>
                                                        arrayHelpers.remove(
                                                          index
                                                        )
                                                      }
                                                    >
                                                      <CircleX />
                                                    </button>
                                                  )}
                                                </div>
                                              </div>
                                            </Col>
                                          </Row>
                                        </Col>
                                      </div>
                                    </div>
                                  )
                                )}

                                <Col lg={12}>
                                  <div className="g_end mt-4">
                                    <button
                                      type="button"
                                      className="main_btn"
                                      onClick={() =>
                                        arrayHelpers.push({
                                          name: "",
                                          nameAr: "",
                                          numberOfSeats: 1,
                                          price: null,
                                          discount: null,
                                          time: {
                                            start: "12:30 AM",
                                            end: "01:30 AM",
                                          },
                                        })
                                      }
                                    >
                                      {t("Add More")}
                                    </button>
                                  </div>
                                </Col>
                              </>
                            )}
                          />
                          {/* </Row> */}
                        </Row>
                      </div>
                    </Col>
                    <Col lg={12} style={{ marginTop: 21 }}>
                      <div className="bg_white br_14 p_23_30">
                        <h2 className="fs_20 mb-4">{t("Amenities")}</h2>
                        <Row>
                          <Col lg={6}>
                            <div className="mb-3 form_field">
                              <CustomLabel
                                htmlFor={`amenities`}
                                label={t("Select Amenities")}
                              />
                              <MultipleSelect
                                options={amenitiesList?.map((item) => ({
                                  name: item?.name,
                                  value: item?._id,
                                }))}
                                value={values.amenities}
                                placeholder={t("Select Amenities")}
                                handleMultipleSelectChange={(e) => {
                                  setFieldValue(`amenities`, e);
                                }}
                                className={`${
                                  touched.amenities && errors.amenities
                                    ? "invalid"
                                    : ""
                                }`}
                              />
                              <ErrorMessage
                                name={`amenities`}
                                component="div"
                                className="text-danger"
                              />
                            </div>
                          </Col>
                        </Row>
                      </div>
                    </Col>
                  </Row>
                  <Row className="px-0 my-4">
                    <FormButtons
                      isDraftBtn={true}
                      isLoading={isLoading}
                      addBtnText={mode === "add" ? t("Add New") : t("Update")}
                    />
                  </Row>
                </Form>
              )}
            </Formik>
          </Container>
        )}
      </div>
    </React.Fragment>
  );
};

export default AddWorkspace;
